import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { EVENT_PRICES as P } from "../slugs/eventPricing";

const monacoGrandPrix: ChauffeurPage = {
  slug: "monaco-grand-prix",
  title: "Private chauffeur Monaco Grand Prix – F1 VTC",
  subtitle:
    "Private chauffeur during the Monaco Grand Prix: timing around circuit closures, Nice ↔ Monaco links and heliport coordination.",
  keywords: [
    "monaco grand prix chauffeur",
    "monaco f1 private chauffeur",
    "monaco grand prix vtc",
  ],
  hero: {
    title: "Private chauffeur during the Monaco Grand Prix",
    subtitle:
      "In late May the street circuit shuts whole avenues: a chauffeur who masters the time windows and the detours.",
    keywords: [
      "Monaco Grand Prix chauffeur",
      "F1 Monaco VTC",
      "Port Hercule paddock chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during the Monaco Grand Prix",
    badges: [
      "Timing before closures",
      "Nice ↔ Monaco link",
      "Heliport coordination",
    ],
  },
  benefits: {
    heading: "Why a chauffeur during the Grand Prix",
    items: [
      {
        title: "Closures mastered",
        description:
          "The street circuit shuts avenues for several days: the chauffeur knows the access windows and the drop-offs still possible before lockdown.",
      },
      {
        title: "Nice ↔ Monaco link",
        description:
          "Access to the Principality is saturated over the weekend: routes anticipated via the corniches or the A8 depending on the closures.",
      },
      {
        title: "Paddock & yachts",
        description:
          "Paddock hospitality, terraces and yachts moored at Port Hercule: drop-off as close as possible despite the barriers.",
      },
      {
        title: "Ultra-VIP clientele",
        description:
          "Executives, teams and prestige guests: absolute discretion, a uniformed chauffeur and punctuality on tight schedules.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during the Monaco Grand Prix",
    services: [
      {
        title: "Nice airport link",
        text: "Nice ↔ Monaco transfer with flight tracking and coordination with the Monaco heliport available.",
      },
      {
        title: "Timing around closures",
        text: "Departures and drop-offs set to the time windows when the circuit roads reopen.",
      },
      {
        title: "Port Hercule access",
        text: "Drop-off and pick-up as close as possible to the yachts and hospitality moored on the port, despite the barriers.",
      },
      {
        title: "Weekend hire",
        text: "A dedicated chauffeur across practice, qualifying and race days to chain without downtime.",
      },
      {
        title: "Evenings & galas",
        text: "After-races, dinners and parties in the Principality or in Cannes, with a guaranteed late-night return.",
      },
    ],
  },
  locations: {
    heading: "Key points during the Grand Prix",
    items: [
      {
        name: "Monaco street circuit",
        description:
          "Sainte-Dévote, the Casino climb, the tunnel, the port chicane.",
      },
      {
        name: "Port Hercule",
        description:
          "Hospitality yachts, terraces and the main reception area.",
      },
      {
        name: "Monte-Carlo & Casino",
        description: "Palaces, paddock club and prestige evenings.",
      },
      {
        name: "Monaco heliport (Fontvieille)",
        description: "Fast links with Nice during the affluence peaks.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description:
          "Terminals 1 and 2, arrivals concentrated on the race weekend.",
      },
    ],
  },
  generic: {
    heading: "The Monaco Grand Prix from a chauffeur's seat",
    subheading:
      "In late May, a circuit drawn through the city: roads close in phases and every trip is planned in advance.",
    paragraphs: [
      "Every late May, the Monaco Grand Prix turns the Principality into a street circuit. From Sainte-Dévote to the port chicane, whole avenues close for several days and access to the quays runs by time windows. A chauffeur who masters these closures drops you as close as possible before lockdown and anticipates the detours.",
      "Access to Monaco is saturated over the weekend. We anticipate the Nice ↔ Monaco links via the corniches or the A8 depending on the closures, and coordinate with the Fontvieille heliport when needed to get past the affluence peaks.",
      "The Grand Prix clientele — teams, partners, prestige guests — chains paddock hospitality, Port Hercule yachts and parties. Hourly hire across practice, qualifying and race days keeps the same chauffeur and secures the late-night returns after the after-races.",
    ],
    bullets: [
      "Drop-offs before lockdown",
      "Corniches or A8 per closures",
      "Yacht-to-circuit at Port Hercule",
    ],
  },
  process: {
    heading: "Booking a chauffeur for the Monaco Grand Prix",
    steps: [
      "Tell us your days (practice, qualifying, race), your accommodation and your paddock/yacht access.",
      "We set departures to the road-reopening windows and the Nice ↔ Monaco links.",
      "The chauffeur arrives in uniform, with flight tracking and heliport coordination if needed.",
      "Weekend hire to chain circuit, hospitality and evenings.",
    ],
  },
  faq: [
    {
      q: "How much does a private chauffeur cost during the Monaco Grand Prix?",
      a: `Journeys to Monaco are fixed prices: from Nice airport, €${P.monacoNiceE} in a Class E, €${P.monacoNiceV} in a Class V (7 seats) and €${P.monacoNiceS} in a Class S; from Cannes, €${P.cannesMonacoE}, €${P.cannesMonacoV} or €${P.cannesMonacoS}. To keep the chauffeur all day, hourly hire starts at €${P.e3} for 3 hours in a Class E, and an 8-hour day costs €${P.e8}, €${P.v8} or €${P.s8} depending on the class. These are our public rates, and the final price is confirmed in writing before the service.`,
    },
    {
      q: "Transfer or hourly hire: which one for the Grand Prix?",
      a: `A transfer is enough if you go to Monaco in the morning and leave in the evening: two fixed-price journeys. Hourly hire keeps the same chauffeur on site when circuit closures make every new journey uncertain, and lets you chain grandstand, yacht and dinner. It starts at 3 hours.`,
    },
    {
      q: "How do I book a chauffeur during the Monaco Grand Prix?",
      a: "Book well ahead: it is one of the tightest weekends on the Riviera. Share your days, your accommodation and your access (paddock, yachts) so we can secure a dedicated chauffeur and plan the trips around the closures.",
    },
    {
      q: "How do you handle the circuit road closures?",
      a: "The street circuit shuts avenues in phases over several days. We set departures and drop-offs to the road-reopening time windows and anticipate the detours to get you as close as possible before lockdown.",
    },
    {
      q: "Is the Nice airport to Monaco link still possible during the race?",
      a: "Yes, but access is saturated. We anticipate the route via the corniches or the A8 depending on the closures, with flight tracking, and can coordinate with the Monaco heliport to get past the affluence peaks.",
    },
    {
      q: "Can you drop off as close as possible to the Port Hercule yachts?",
      a: "Yes, within the barriers and security perimeters. The chauffeur drops you at the nearest authorised point to the yachts and hospitality, then collects you on the way out within the access windows.",
    },
  ],
  relatedLinks: [
    {
      label: "Monaco Nice Airport shuttle",
      href: "/en/chauffeur/transfers/monaco-nice-airport",
    },
    {
      label: "Cannes Monaco shuttle",
      href: "/en/chauffeur/transfers/cannes-monaco",
    },
    {
      label: "Book a journey",
      href: "/en/reservation",
    },
  ],
  cta: {
    title: "Your chauffeur for the Monaco Grand Prix",
    text: "Timing around closures, Nice ↔ Monaco links and heliport: book a dedicated chauffeur for the weekend.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: `Monaco Grand Prix chauffeur from €${P.monacoNiceE} | KS Rent Services`,
    description: `Monaco Grand Prix: Nice airport ⇄ Monaco from €${P.monacoNiceE}, Cannes ⇄ Monaco from €${P.cannesMonacoE}, hourly chauffeur from €${P.e3} for 3 hours. Prices fixed in advance.`,
    canonical: "/en/chauffeur/monaco-grand-prix",
  },
};

export default monacoGrandPrix;
